import type { MantineThemeOverride } from '@mantine/core';
import { z } from 'zod';

import { CogniteLogo } from '#core/components/CogniteLogo.tsx';
import type { config as defaultConfig } from '#core/config.default.ts';
import type { Overwrite } from '#core/schema/utils.ts';

const BaseFeatureSchema = z.object({
  enabled: z.boolean().default(false),
});

export const DebugSchema = z
  .object({
    devtools: z.object({ enabled: z.boolean().default(import.meta.env.DEV ?? false) }).prefault({}),
  })
  .prefault({});

export const AiFeatureSchema = BaseFeatureSchema.extend({
  model: z.string().default('azure/gpt-4.1').describe('Default AI model to use for chat completions.'),
  maxTokens: z.number().optional().default(16_384).describe('Default max tokens for AI model responses.'),
  temperature: z.number().optional().default(0.1).describe('Default temperature for AI model responses.'),
}).prefault({});

export const ProjectConfigSchema = z.object({
  id: z.string().describe('Project ID, e.g. "client-dashboard-ui"'),
  name: z.string().describe('Project name, e.g. "Client Name Production Dashboard"'),
  logo: z
    .custom<React.ComponentType>()
    .optional()
    .prefault(() => CogniteLogo)
    .describe('Logo component'),
  faviconUrl: z.string().optional().describe('Favicon URL'),
  theme: z.custom<MantineThemeOverride>().optional(),
  defaultColorScheme: z.enum(['light', 'dark']).optional().default('light'),
  forceColorScheme: z.enum(['light', 'dark']).optional(),
  debug: DebugSchema,
  feature: z
    .object({
      ai: AiFeatureSchema,
    })
    .prefault({}),
});

// biome-ignore lint/suspicious/noEmptyInterface: Overrides in root project
export interface ProjectConfigOverrides {}

type DefaultConfigType = typeof defaultConfig;

type BaseProjectConfig = Overwrite<z.output<typeof ProjectConfigSchema>, DefaultConfigType>;
type MergedProjectConfig = Overwrite<BaseProjectConfig, ProjectConfigOverrides>;

/** @public */
export interface ProjectConfigInput extends z.input<typeof ProjectConfigSchema> {}
/** @public */
export type ProjectConfigOutput = z.util.Prettify<MergedProjectConfig>;
